/**
 * Campaign creation progress.
 *
 * Replaces a row of five pills, which read as generic wizard chrome and gave the
 * active step no more emphasis than the rest. This is one bordered rail split by
 * hairlines: the numbers carry the sequence, a gold rule marks where the operator
 * is, and completed steps get a filled marker with a tick.
 *
 * Navigation behaviour is unchanged - a step is reachable once the campaign
 * exists, and forward steps stay disabled on a brand-new draft.
 */
import { Check } from 'lucide-react'

type Props = {
  steps: readonly string[]
  current: number
  /** Steps ahead of `current` are only reachable once the draft has been saved. */
  unlocked: boolean
  onSelect: (index: number) => void
}

export default function StepRail({ steps, current, unlocked, onSelect }: Props) {
  return (
    <nav className="ntf-steps" aria-label="Campaign creation steps">
      {steps.map((label, index) => {
        const isActive = index === current
        const isDone = index < current
        const locked = index > current && !unlocked

        return (
          <button
            key={label}
            type="button"
            className={`ntf-step${isActive ? ' is-active' : ''}${isDone ? ' is-done' : ''}`}
            disabled={locked}
            aria-current={isActive ? 'step' : undefined}
            onClick={() => onSelect(index)}
          >
            <span className="ntf-step__marker" aria-hidden>
              {isDone ? <Check size={12} strokeWidth={3} /> : index + 1}
            </span>
            <span className="ntf-step__label">{label}</span>
          </button>
        )
      })}
    </nav>
  )
}
